import { Box, Grid, LinearProgress } from "@mui/material";
import ThemeContext from "components/app/Context";
import Filters from "components/common/Filters";
import FilterDescription from "components/common/Filters/FilterDescription";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import SearchAppBar from "components/common/searchBar";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { DEFAULT_CORP_INNCODE, messages } from "utils/CommonConstants";
import {
  filterPayloadToPlanProgressPayload,
  getOwnerNames,
  getRoleCategory,
  getSourceName,
  getTableColor
} from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import {
  fetchAssigneeDetailsByHtlIdsAsync,
  fetchMyPlansAsync,
  fetchSnapshotData,
  setLoader
} from "../AboveProperty/AboveProperty.slice";

export const headers = [
  { content: "Property" },
  { content: "Inncode" },
  { content: "Owner(s)" },
  { content: "Role Category" },
  { content: "Source" },
  { content: "Status" },
  { content: "Deadline" },
  { content: "Due Date" }
];

let isMounted = false;
const PlanProgressList = () => {
  const dispatch = useAppDispatch();
  const [pageNo, setPageNo] = useState(0);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<any>({ searchText: "", isModified: false });
  const location = useLocation();
  const params = useParams();
  const innCode = useAppSelector((state: any) => state.app.innCode);
  const filterCategories = useAppSelector((state) => state.filters.filterCategories);
  const currentParent = useAppSelector((state) => state.filters.currentParent);
  const { isMobileScreen } = useContext(ThemeContext);

  const {
    totalList,
    plansList: userplans,
    inncodes: inncodesList,
    snapshotData
  } = useAppSelector((state) => state.aboveProperty.planProgress);
  const filterRequestPayload = useAppSelector((state) => state.filters.filterRequestPayload);
  // const filterPaylo
  const getPlanProps = (startPage?: number) => {
    if (startPage === 0) setPageNo(startPage);
    const props = {
      innCode: getInncodeValue(),
      includeOwners: true,
      includeFilters: true,
      cmsId: params.id,
      searchedPropertyText: searchText?.searchText,
      ...filterPayloadToPlanProgressPayload(filterRequestPayload)
    };

    const page = startPage ?? pageNo;
    const payload = { ...props };
    return { payload, page };
  };

  const getInncodeValue = () => {
    if (location?.state?.innCodes && !location?.state?.innCode) return location?.state?.innCodes.join(",");
    else if (location?.state?.innCode)
      return location?.state?.innCode === DEFAULT_CORP_INNCODE ? "" : location?.state?.innCode;
    else if (innCode === DEFAULT_CORP_INNCODE) return "";
    else return innCode;
  };

  const getSnapshotProps = () => {
    return {
      innCode: getInncodeValue(),
      includeOwners: true,
      cmsId: params.id,
      fetchSnapshot: true,
      ...filterPayloadToPlanProgressPayload(filterRequestPayload)
    };
  };
  const getPlanProgress = async (props: any) => {
    const plans = await dispatch(fetchMyPlansAsync(props));
    const planOwners = plans?.payload?.meta?.owners;
    if (planOwners?.length) {
      const data = {
        value: planOwners.join(","),
        size: planOwners?.length
      };
      if (!isMounted) await dispatch(fetchAssigneeDetailsByHtlIdsAsync(data));
      isMounted = true;
    }
    await dispatch(fetchSnapshotData(getSnapshotProps()));
    dispatch(setLoader(false));
  };

  useEffect(() => {
    if (["planProgressFromMyListDisplay", "planProgressFromLibraryDisplay"].includes(currentParent))
      getPlanProgress(getPlanProps(0));
  }, [filterRequestPayload, searchText]);

  useEffect(() => {
    if (pageNo > 0) getPlanProgress(getPlanProps());
  }, [pageNo]);

  const fetchPlans = async (props: any) => {
    await dispatch(fetchMyPlansAsync(getPlanProps(0)));
    dispatch(setLoader(false));
  };
  useEffect(() => {
    if (searchText?.isModified) fetchPlans(getPlanProps(0));
  }, [searchText]);

  const getInputVal = (val: string) => {
    setSearchText({ searchText: val, isModified: true });
  };
  const getDateColor = (date: any, status: string) => {
    const todaysDate = moment().format("YYYY-MM-DD");
    return moment(date).isBefore(todaysDate) && status !== "Completed"
      ? tokens.Text.Feedback.Alert
      : tokens.Text.General.PrimaryHeadline;
  };
  const getMetricsData = () => {
    return [
      { isPrimary: true, subTitle: "New", data: snapshotData?.newMyPlansCount },
      { subTitle: "In Progress", data: snapshotData?.inprogressMyPlansCount, showBorder: true },
      { subTitle: "Completed", data: snapshotData?.completedMyPlansCount, showBorder: true },
      { subTitle: "Overdue", data: snapshotData?.overDueMyPlansCount }
    ];
  };
  return (
    <>
      <Grid
        sx={{
          padding: "24px",
          backgroundColor: "white",
          borderRadius: "8px"
        }}
      >
        <Grid
          gap={isMobileScreen ? "10px" : "none"}
          display={"flex"}
          flexDirection={isMobileScreen ? "column" : "row"}
          width={"100%"}
        >
          <SearchAppBar
            value={""}
            getInputDetails={(val: string) => getInputVal(val)}
            placeholder={"Search by property"}
            sx={{ minWidth: isMobileScreen ? "none" : "350px", border: `solid 1px ${tokens.Border.General.Tertiary}` }}
          />
          <Box marginLeft={"auto"}>
            <Filters
              filters={filterCategories}
              parentPage={
                location?.state?.isMyLists ? "planProgressFromMyListDisplay" : "planProgressFromLibraryDisplay"
              }
            />
          </Box>
        </Grid>
        <FilterDescription />
        <Grid display={"flex"} flexDirection={"column"} gap="20px" marginTop={isMobileScreen ? "10px" : "24px"}>
          <KeyMetrics keyMetricsData={getMetricsData()} />
          <BasicTable
            id="tableDiv"
            totalRecords={totalList}
            pagination={{ pageNo, setPageNo }}
            noDataMessage={messages.noPropertyPlans}
            data={{
              headers,
              rows: userplans?.map((element: any) => {
                const progressVal = element?.cardsCompleted / element?.totalCards;
                return {
                  data: [
                    {
                      content: (
                        <Box
                          onClick={() => navigate(`/myplandetails/${element?.id}`)}
                          sx={{
                            textDecoration: "none",
                            cursor: "pointer",
                            color: tokens.Text.General.PrimaryHeadline,
                            fontWeight: "700"
                          }}
                        >
                          {element?.propertyName}
                        </Box>
                      ),
                      showBold: true
                    },

                    { content: element?.innCode },
                    {
                      content: getOwnerNames(element?.owners)
                    },

                    { content: getRoleCategory(element?.owners) ?? "-" },
                    { content: getSourceName(element?.planSource) },

                    {
                      content: (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start"
                          }}
                        >
                          <Typography variant="body2" color={getTableColor(element?.status)}>
                            {" "}
                            {element?.status}
                          </Typography>
                          {element?.status !== "New" && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center"
                              }}
                            >
                              {" "}
                              <LinearProgress
                                sx={{ width: "80px", height: "6px" }}
                                variant="determinate"
                                value={!isNaN(progressVal) ? progressVal * 100 : 0}
                              />
                              <Typography component={"span"}>
                                {" "}
                                {!isNaN(progressVal) ? `${Math.round(progressVal * 100)}%` : `0%`}
                              </Typography>{" "}
                            </Box>
                          )}
                        </Box>
                      )
                    },

                    {
                      content: getUIFormatDate(element?.brandDeadlineDate),
                      color: moment(element?.brandDeadlineDate).isBefore(new Date()) ? tokens.Text.Feedback.Alert : ""
                    },
                    {
                      content: getUIFormatDate(element?.dueDate),
                      color: getDateColor(element?.dueDate, element?.status)
                    }
                  ]
                };
              })
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
export default PlanProgressList;
